/**
 * F45 (Wave 2D) — geographic distribution page.
 *
 * Route: `/analytics/geographic`. Permission: `analytics:read`.
 *
 * City-level booking intensity (engineering-authored CityIntensity bars — see
 * the component for why MapView is not used: the frozen AnalyticsCityRow carries
 * no coordinates, D15 aggregate-only) + a sortable city Table. DateRangePicker
 * bounds the window.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  DateRangePicker,
  Skeleton,
  Table,
  type DateRange,
  type TableColumn,
} from '@bravemove/ui-web';
import type { AnalyticsCityRow, AnalyticsGeographic } from '@bravemove/types';
import { adminAnalyticsApi } from '../../api/analytics';
import { PageHeader } from '../../components/PageHeader';
import { AnalyticsNav } from './AnalyticsNav';
import { CityIntensity } from './components/CityIntensity';
import { defaultAnalyticsRange, type AnalyticsRangeValue } from './analyticsHelpers';

export function AnalyticsGeographicPage() {
  const { t, locale } = useI18n();

  const [range, setRange] = useState<AnalyticsRangeValue>(() => defaultAnalyticsRange());
  const [geo, setGeo] = useState<AnalyticsGeographic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGeo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setGeo(await adminAnalyticsApi.getGeographic({ from: range.from, to: range.to }));
    } catch {
      setError(t('admin:analytics.error'));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchGeo();
  }, [fetchGeo]);

  const columns = useMemo<readonly TableColumn<AnalyticsCityRow>[]>(
    () => [
      {
        key: 'city',
        header: t('admin:analytics.geographic.city'),
        sortable: true,
        sortAccessor: (r) => r.cityName,
        cell: (r) => <span className="text-[13px] text-ink">{r.cityName}</span>,
      },
      {
        key: 'bookings',
        header: t('admin:analytics.geographic.bookings'),
        align: 'end',
        sortable: true,
        sortAccessor: (r) => r.count,
        cell: (r) => <span className="font-mono tabular-nums">{r.count.toLocaleString()}</span>,
      },
    ],
    [t],
  );

  const onCustomRange = (dr: DateRange) =>
    setRange((prev) => ({ from: dr.start ?? prev.from, to: dr.end ?? prev.to }));

  return (
    <div className="space-y-6" data-testid="analytics-geographic-page">
      <PageHeader eyebrow="Analytics" title={t('admin:analytics.geographic.title')} />
      <AnalyticsNav />

      <DateRangePicker
        value={{ start: range.from, end: range.to }}
        onChange={onCustomRange}
        locale={locale}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading || !geo ? (
        <Skeleton className="h-72 w-full rounded-xl" data-testid="analytics-geographic-loading" />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <section className="rounded-xl border border-hairline bg-surface p-4">
            <h2 className="mb-3 font-mono text-[11px] font-bold uppercase tracking-wider text-ink-3">
              {t('admin:analytics.geographic.title')}
            </h2>
            <CityIntensity cities={geo.cities} ariaLabel={t('admin:analytics.geographic.title')} />
          </section>
          <div>
            <Table
              data={geo.cities as AnalyticsCityRow[]}
              columns={columns as TableColumn<AnalyticsCityRow>[]}
              rowKey={(r) => r.cityCode}
              density="compact"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalyticsGeographicPage;
